import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { PencilEditIcon } from '../../icons';
import PlusIcon from '../../icons/PlusIcon';
import AddInfluencesModal from '../../modals/AddInfluencesModal';
import { SpotifyArtistType } from '../../../types/spotify';
import { PlaceholderLabel } from '../PlaceholderLabel';
import axiosService from '../../../utils/axios';
import { artistImageMemory, artistWithImage, rememberArtistImages } from '../../../pages/Onboarding/artistImages';
import GenreLabel from '../../GenreLabel';
import { formatGenreLabel, formatGenreList } from '../../../utils/genreLabel';
import { genreColorMap } from './genreColors';
import { genreTagWidths, highlightRegions, HighlightRegion, PortraitBox } from './highlightLayout';
import { arrangeInfluenceArtists } from './influenceOrder';
import { PROFILE_MODULE_CLASS } from '@/theme/surfaceClasses';
import {
    buildInfluencePreview,
    CatalogGroup,
    influenceRowTemplate,
    placeholderCatalog,
    previewColumns,
} from './emptyPreview';
import { loadPreviewCatalog, peekPreviewCatalog } from './previewCatalog';
import { useNightMode } from '@/theme/useNightMode';

interface InfluencePropType {
  selectedArtists: SpotifyArtistType[],
  selectedGenres: string[],
  profileId?: string,
  onSaved: (update: { favArtists: SpotifyArtistType[]; favGenres: string[] }) => void,
  canEdit?: boolean,
}

interface InfluenceArtist {
  id?: string | null
  name?: string
  displayText?: string
  url?: string | null
  image?: string | null
  scores?: number[]
  subgenres?: string[]
}

interface InfluenceGroup {
  genre: string
  artists: InfluenceArtist[]
}

interface InfluenceDisplay {
  genreOrder?: string[]
  groups: InfluenceGroup[]
  looseArtists: InfluenceArtist[]
  looseGenres: string[]
}

const displayCache = new Map<string, InfluenceDisplay>();

export function clearInfluenceGalleryCache() {
    displayCache.clear();
}

const artistLabel = (artist: InfluenceArtist) => artist.displayText || artist.name || 'Artist';

const pictured = (artist: InfluenceArtist): InfluenceArtist => {
    const next = artistWithImage(artistImageMemory(), artist);
    rememberArtistImages(artistImageMemory(), [next]);
    return next;
};

function isInfluenceDisplay(value: unknown): value is InfluenceDisplay {
    if (!value || typeof value !== 'object') return false;
    const display = value as InfluenceDisplay;
    return Array.isArray(display.groups) && Array.isArray(display.looseArtists) && Array.isArray(display.looseGenres);
}

function cleanSubgenres(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    const seen = new Set<string>();
    const labels: string[] = [];
    value.forEach((genre) => {
        if (typeof genre !== 'string') return;
        const label = genre.trim();
        const key = label.toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        labels.push(label);
    });
    return labels;
}

function presentArtist(artist: InfluenceArtist): InfluenceArtist {
    const next = pictured({
        ...artist,
        subgenres: cleanSubgenres(artist.subgenres),
    });
    if (!Array.isArray(artist.scores)) return next;
    return {
        ...next,
        scores: artist.scores.map((score) => (
            typeof score === 'number' && Number.isFinite(score) && score > 0 ? score : 0
        )),
    };
}

function normalizeDisplay(raw: InfluenceDisplay): InfluenceDisplay {
    return {
        genreOrder: Array.isArray(raw.genreOrder) ? raw.genreOrder.filter(Boolean) : undefined,
        groups: raw.groups
            .filter((group) => group?.genre && Array.isArray(group.artists))
            .map((group) => ({
                genre: group.genre,
                artists: group.artists.map(presentArtist),
            }))
            .filter((group) => group.artists.length > 0),
        looseArtists: raw.looseArtists.map(presentArtist),
        looseGenres: raw.looseGenres.filter(Boolean),
    };
}

function fromSaved(artist: SpotifyArtistType): InfluenceArtist {
    return pictured({
        id: artist.id,
        name: artist.name,
        displayText: artist.displayText || artist.name,
        url: artist.url || artist.external_urls?.spotify || null,
        image: null,
    });
}

function requestKeyFor(artists: SpotifyArtistType[], genres: string[]) {
    return JSON.stringify({
        artists: artists.map((artist) => ({
            id: artist.id || '',
            name: artist.name || '',
            displayText: artist.displayText || '',
            url: artist.url || artist.external_urls?.spotify || '',
        })),
        genres,
    });
}

function ArtistPortrait({ artist, loading, ink }: {
    artist: InfluenceArtist
    loading: boolean
    ink?: string
}) {
    const label = artistLabel(artist);
    const initial = label.trim().charAt(0).toUpperCase() || '?';
    const [failed, setFailed] = useState(false);
    const showImage = Boolean(artist.image) && !failed;
    const subgenres = artist.subgenres || [];
    const subgenreList = formatGenreList(subgenres);
    const describedBy = useId();
    const { night } = useNightMode();

    useEffect(() => {
        setFailed(false);
    }, [artist.image]);

    const face = (
        <span
            className={`mx-auto block h-20 w-20 overflow-hidden rounded-full bg-gray-100 ring-1 ring-black/10 ${artist.url ? 'transition-shadow group-hover:ring-2 group-hover:ring-purple-light' : ''}`}
        >
            {showImage ? (
                <img
                    src={artist.image || ''}
                    alt={label}
                    loading="lazy"
                    className="h-full w-full object-cover"
                    onError={() => setFailed(true)}
                />
            ) : loading ? (
                <span className="block h-full w-full animate-pulse bg-gray-200 motion-reduce:animate-none" />
            ) : (
                <span className="flex h-full w-full items-center justify-center bg-purple-light/10 text-xl font-bold text-purple-light">
                    {initial}
                </span>
            )}
        </span>
    );
    const body = (
        <>
            {face}
            <span
                className={`mt-1.5 block h-8 break-words text-center text-xs font-semibold leading-4 text-gray-800 line-clamp-2 dark:text-black ${artist.url ? 'group-hover:text-purple-light dark:group-hover:text-purple-light' : ''}`}
                title={label}
            >
                {label}
            </span>
            {subgenreList && (
                <span id={describedBy} className="sr-only">Related genres: {subgenreList}</span>
            )}
        </>
    );
    const className = 'block w-full min-w-0 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-light/40';
    const described = subgenreList ? describedBy : undefined;
    const portrait = artist.url ? (
        <a
            className={`group ${className}`}
            href={artist.url}
            target="_blank"
            rel="noreferrer noopener"
            aria-label={`${label} on Spotify`}
            aria-describedby={described}
        >
            {body}
        </a>
    ) : (
        <div className={className} aria-describedby={described}>{body}</div>
    );
    if (!subgenreList) return portrait;
    return (
        <div className="group/subgenre relative w-full min-w-0">
            {portrait}
            <div
                role="tooltip"
                aria-hidden="true"
                className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-1 hidden w-max max-w-[11rem] -translate-x-1/2 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-center shadow-lg group-hover/subgenre:block group-focus-within/subgenre:block"
            >
                <p
                    className="text-[10px] font-semibold uppercase tracking-wide"
                    style={{ color: night ? 'rgb(243 116 141)' : (ink || '#4b5563') }}
                >
                    Related Genres
                </p>
                <p className="mt-0.5 text-xs font-semibold leading-4 text-gray-900">{subgenreList}</p>
            </div>
            <p className="mt-0.5 w-full min-w-0 max-w-full truncate text-center text-[10px] font-medium leading-3 text-gray-500 [@media(hover:hover)]:sr-only">
                {subgenreList}
            </p>
        </div>
    );
}

interface TaggedArtist extends InfluenceArtist {
    genre: string | null
}

const SPOTIFY_ARTIST_ID = /^[0-9A-Za-z]{10,40}$/;

function spotifyArtistId(value: unknown): string | null {
    const id = String(value || '').trim();
    return SPOTIFY_ARTIST_ID.test(id) ? id : null;
}

/** Older profiles store the Spotify artist URL and leave the id empty. */
function idFromSpotifyUrl(url: unknown): string | null {
    const value = String(url || '').trim();
    if (!/^https:\/\//i.test(value)) return null;
    let parsed: URL;
    try {
        parsed = new URL(value);
    } catch {
        return null;
    }
    if (parsed.hostname !== 'open.spotify.com') return null;
    const parts = parsed.pathname.split('/').filter(Boolean);
    const artistAt = parts.findIndex((part) => part.toLowerCase() === 'artist');
    if (artistAt < 0) return null;
    return spotifyArtistId(parts[artistAt + 1]);
}

function artistKey(artist: {
    id?: string | null
    name?: string
    displayText?: string
    url?: string | null
    external_urls?: { spotify?: string | null }
}) {
    const id = spotifyArtistId(artist.id)
        || idFromSpotifyUrl(artist.url)
        || idFromSpotifyUrl(artist.external_urls?.spotify);
    if (id) return `id:${id}`;
    const name = (artist.displayText || artist.name || '').trim().toLowerCase();
    return name ? `name:${name}` : '';
}

function galleryArtists(selected: SpotifyArtistType[], display: InfluenceDisplay): TaggedArtist[] {
    const genreOf = new Map<string, string>();
    const enriched = new Map<string, InfluenceArtist>();
    const remember = (artist: InfluenceArtist, genre: string | null) => {
        const key = artistKey(artist);
        if (!key) return;
        if (!enriched.has(key)) enriched.set(key, artist);
        if (genre) genreOf.set(key, genre);
    };
    display.groups.forEach((group) => {
        group.artists.forEach((artist) => remember(artist, group.genre));
    });
    display.looseArtists.forEach((artist) => remember(artist, null));
    const ordered = selected.length > 0
        ? selected
        : [...display.groups.flatMap((group) => group.artists), ...display.looseArtists];
    const tagged = ordered.map((artist) => {
        const key = artistKey(artist);
        const known = key ? enriched.get(key) : undefined;
        const genre = key ? genreOf.get(key) || null : null;
        if (known) return { ...known, genre };
        return { ...fromSaved(artist as SpotifyArtistType), genre };
    });
    return arrangeInfluenceArtists(tagged);
}

function genreOrderOf(display: InfluenceDisplay) {
    const seen = new Set<string>();
    const order: string[] = [];
    const add = (genre: string) => {
        const label = genre.trim();
        const key = label.toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        order.push(label);
    };
    (display.genreOrder || []).forEach(add);
    display.groups.forEach((group) => add(group.genre));
    display.looseGenres.forEach(add);
    return order;
}

function readPortraits(frame: HTMLElement): PortraitBox[] {
    const origin = frame.getBoundingClientRect();
    return Array.from(frame.querySelectorAll<HTMLElement>('[data-influence-portrait]')).map((item) => {
        const rect = item.getBoundingClientRect();
        return {
            genre: item.dataset.genre || null,
            left: rect.left - origin.left,
            top: rect.top - origin.top,
            width: rect.width,
            height: rect.height,
        };
    });
}

function PortraitList({ artists, loading, colors }: {
    artists: TaggedArtist[]
    loading: boolean
    colors: ReturnType<typeof genreColorMap>
}) {
    const frameRef = useRef<HTMLDivElement>(null);
    const [regions, setRegions] = useState<HighlightRegion[]>([]);
    const [tagWidths, setTagWidths] = useState<number[]>([]);
    const grouped = artists.some((artist) => artist.genre);
    const portraitKey = artists.map((artist, index) => (
        `${artist.id || artistLabel(artist)}:${artist.genre || ''}:${index}`
    )).join('|');

    useLayoutEffect(() => {
        const frame = frameRef.current;
        if (!frame || !grouped) {
            setRegions((current) => (current.length === 0 ? current : []));
            setTagWidths((current) => (current.length === 0 ? current : []));
            return undefined;
        }
        const measure = () => {
            const portraits = readPortraits(frame);
            const next = highlightRegions(portraits);
            const widths = genreTagWidths(portraits);
            setRegions((current) => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
            setTagWidths((current) => (
                current.length === widths.length && current.every((width, index) => width === widths[index])
                    ? current
                    : widths
            ));
        };
        measure();
        if (typeof ResizeObserver !== 'function') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(frame);
        frame.querySelectorAll('[data-influence-portrait]').forEach((item) => observer.observe(item));
        return () => observer.disconnect();
    }, [grouped, portraitKey]);

    return (
        <div ref={frameRef} className="relative">
            {regions.length > 0 && (
                <div className="pointer-events-none absolute inset-0" aria-hidden>
                    {regions.map((region, index) => {
                        const color = colors.get(region.genre.toLowerCase());
                        return (
                            <div
                                key={`${region.genre}-${index}`}
                                style={{
                                    position: 'absolute',
                                    left: region.left,
                                    top: region.top,
                                    width: region.width,
                                    height: region.height,
                                    background: color?.wash,
                                    borderTopLeftRadius: region.corners.topLeft,
                                    borderTopRightRadius: region.corners.topRight,
                                    borderBottomRightRadius: region.corners.bottomRight,
                                    borderBottomLeftRadius: region.corners.bottomLeft,
                                }}
                            />
                        );
                    })}
                </div>
            )}
            <ul
                className="relative z-10 m-0 grid w-full list-none justify-start gap-x-2 gap-y-4 p-0"
                style={{ gridTemplateColumns: influenceRowTemplate() }}
            >
                {artists.map((artist, index) => {
                    const runStart = Boolean(artist.genre) && artists[index - 1]?.genre !== artist.genre;
                    const color = artist.genre ? colors.get(artist.genre.toLowerCase()) : undefined;
                    return (
                        <li
                            key={`${artist.id || artistLabel(artist)}-${index}`}
                            className="relative min-w-0 hover:z-30 focus-within:z-30"
                            data-influence-portrait=""
                            data-genre={artist.genre || undefined}
                        >
                            {artist.genre && !runStart && (
                                <span className="sr-only"><GenreLabel genre={artist.genre} /></span>
                            )}
                            {runStart && artist.genre && color && (
                                <span
                                    className="pointer-events-none absolute left-0 top-0 z-20 box-border max-w-full -translate-y-1/2 truncate rounded-full border border-solid bg-white px-2 py-0.5 text-xs font-semibold leading-none dark:bg-white"
                                    style={{
                                        color: color.ink,
                                        borderColor: color.ink,
                                        ...(tagWidths[index] > 0 ? { maxWidth: tagWidths[index] } : {}),
                                    }}
                                    title={formatGenreLabel(artist.genre)}
                                >
                                    <GenreLabel genre={artist.genre} />
                                </span>
                            )}
                            <ArtistPortrait artist={artist} loading={loading} ink={color?.ink} />
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function GenreChip({ genre }: { genre: string }) {
    return (
        <span className="inline-flex items-center rounded-full border border-purple-light/30 bg-white px-3.5 py-2 text-sm font-semibold text-purple-light dark:bg-white dark:text-purple-light">
            <GenreLabel genre={genre} />
        </span>
    );
}

function GenreRows({ genres, label = 'Genres' }: { genres: string[], label?: string }) {
    if (genres.length === 0) return null;
    return (
        <ul className="m-0 flex list-none flex-wrap gap-2 p-0" aria-label={label}>
            {genres.map((genre) => (
                <li key={genre}>
                    <GenreChip genre={genre} />
                </li>
            ))}
        </ul>
    );
}

function InfluenceGallery({ display, loading, selectedArtists }: {
    display: InfluenceDisplay
    loading: boolean
    selectedArtists: SpotifyArtistType[]
}) {
    const artists = galleryArtists(selectedArtists, display);
    const colors = genreColorMap(genreOrderOf(display));
    if (artists.length === 0) return <GenreRows genres={display.looseGenres} />;
    return (
        <div
            className={display.looseGenres.length > 0 ? 'flex flex-col gap-4' : undefined}
            aria-busy={loading || undefined}
        >
            <PortraitList artists={artists} loading={loading} colors={colors} />
            {display.looseGenres.length > 0 && <GenreRows genres={display.looseGenres} />}
        </div>
    );
}

const previewPlaceholder = placeholderCatalog();

function previewSeed() {
    const key = 'jamwith.influencePreviewSeed';
    try {
        const existing = Number(sessionStorage.getItem(key));
        if (Number.isFinite(existing) && existing > 0) return existing;
        const seed = Math.floor(Math.random() * 1_000_000_000) + 1;
        sessionStorage.setItem(key, String(seed));
        return seed;
    } catch {
        return 1;
    }
}

function usePreviewColumns(frameRef: { current: HTMLDivElement | null }) {
    const [columns, setColumns] = useState(6);

    useLayoutEffect(() => {
        const frame = frameRef.current;
        if (!frame) return undefined;
        const apply = () => {
            const next = previewColumns(frame);
            if (next > 0) setColumns((current) => (current === next ? current : next));
        };
        apply();
        if (typeof ResizeObserver !== 'function') return undefined;
        const observer = new ResizeObserver(apply);
        observer.observe(frame);
        return () => observer.disconnect();
    }, [frameRef]);

    return columns;
}

function EmptyInfluences({ onAdd }: { onAdd: () => void }) {
    const frameRef = useRef<HTMLDivElement>(null);
    const columns = usePreviewColumns(frameRef);
    const [seed] = useState(previewSeed);
    const [catalog, setCatalog] = useState<CatalogGroup[] | null>(() => peekPreviewCatalog(seed));
    const [failed, setFailed] = useState(false);
    const source = catalog?.length ? catalog : (failed ? null : previewPlaceholder);
    const preview = useMemo(
        () => buildInfluencePreview(columns, source, seed),
        [columns, source, seed],
    );
    const colors = genreColorMap(preview.genreOrder);
    const waitingForPhotos = !catalog?.length && !failed;

    useEffect(() => {
        let cancelled = false;
        loadPreviewCatalog(seed).then((groups) => {
            if (cancelled) return;
            if (groups.length) {
                rememberArtistImages(artistImageMemory(), groups.flatMap((group) => (
                    group.artists.map((artist) => ({
                        id: artist.id,
                        name: artist.name,
                        displayText: artist.name,
                        image: artist.image,
                    }))
                )));
                setCatalog(groups);
                return;
            }
            setFailed(true);
        });
        return () => {
            cancelled = true;
        };
    }, [seed]);

    return (
        <div className="group relative overflow-hidden rounded-xl">
            <div
                ref={frameRef}
                className="pointer-events-none select-none px-1 pb-1 pt-4 blur-[8px] transition-[filter] duration-300 group-hover:blur-[6px] motion-reduce:transition-none"
                aria-hidden
                data-influence-preview=""
            >
                <PortraitList artists={preview.artists} loading={waitingForPhotos} colors={colors} />
                {preview.looseGenres.length > 0 && (
                    <div className="mt-4">
                        <GenreRows genres={preview.looseGenres} />
                    </div>
                )}
            </div>
            <button
                type="button"
                className="absolute inset-0 z-10 flex cursor-pointer flex-col items-center justify-center rounded-xl transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-purple-light/50"
                aria-label="Add your influences. Find people with your taste"
                onClick={onAdd}
            >
                <span className="flex flex-col items-center rounded-2xl bg-white/90 px-5 py-2.5 text-gray-800 shadow-md ring-1 ring-black/5">
                    <PlusIcon
                        width={23}
                        height={33}
                    />
                    <PlaceholderLabel className="text-sm" title="Add your influences" benefit="Find people with your taste" />
                </span>
            </button>
        </div>
    );
}

function Influences({ selectedArtists = [], selectedGenres = [], profileId, onSaved, canEdit = false }: InfluencePropType) {
    const [showAddInfluenceModal, setShowAddInfluenceModal] = useState<boolean>(false);
    const requestKey = requestKeyFor(selectedArtists, selectedGenres);
    const cached = displayCache.get(requestKey) || null;
    const [snapshot, setSnapshot] = useState<{ key: string, display: InfluenceDisplay | null, failed: boolean }>(() => ({
        key: requestKey,
        display: cached,
        failed: false,
    }));

    const openInfluences = () => {
        setShowAddInfluenceModal(true);
    };

    useEffect(() => {
        if (selectedArtists.length === 0) return undefined;
        let cancelled = false;
        const artists = selectedArtists.map((artist) => ({
            id: artist.id,
            name: artist.name,
            displayText: artist.displayText || artist.name,
            url: artist.url || artist.external_urls?.spotify || null,
        }));
        axiosService.post('/spotify/influence-display', { artists, genres: selectedGenres }).then((response) => {
            if (cancelled) return;
            if (!isInfluenceDisplay(response?.data)) {
                if (!displayCache.has(requestKey)) setSnapshot({ key: requestKey, display: null, failed: true });
                return;
            }
            const next = normalizeDisplay(response.data);
            displayCache.set(requestKey, next);
            setSnapshot({ key: requestKey, display: next, failed: false });
        }).catch(() => {
            if (cancelled || displayCache.has(requestKey)) return;
            setSnapshot({ key: requestKey, display: null, failed: true });
        });
        return () => {
            cancelled = true;
        };
    // requestKey already covers the artist and genre fields this effect reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    const isEmpty = selectedArtists.length === 0 && selectedGenres.length === 0;
    const hasSnapshot = snapshot.key === requestKey;
    const resolved = selectedArtists.length === 0
        ? { groups: [], looseArtists: [], looseGenres: selectedGenres }
        : (hasSnapshot ? snapshot.display : cached);
    const loading = selectedArtists.length > 0 && !resolved && !(hasSnapshot && snapshot.failed);
    const display: InfluenceDisplay = resolved || {
        groups: [],
        looseArtists: selectedArtists.map(fromSaved),
        looseGenres: hasSnapshot && snapshot.failed ? selectedGenres : [],
    };

    if (isEmpty && !canEdit) return <></>;

    return (
        <div className={`${PROFILE_MODULE_CLASS} flex flex-col p-4`}>
            <AddInfluencesModal
                show={showAddInfluenceModal}
                setShow={setShowAddInfluenceModal}
                onClose={() => setShowAddInfluenceModal(false)}
                profileId={profileId}
                selectedArtists={selectedArtists}
                selectedGenres={selectedGenres}
                onSaved={onSaved}
            />
            <div className="mb-3 flex flex-row items-center justify-between">
                <h2 className="text-lg font-bold text-eerieblack">Influences</h2>
                {canEdit && (
                    <button
                        type="button"
                        className="bg-white p-[6px] rounded-full border-gray-400 border-[1px] cursor-pointer"
                        aria-label="Edit influences"
                        onClick={openInfluences}
                    >
                        <PencilEditIcon
                            width={24}
                            height={24}
                            fill={'#626262'}
                            fillOpacity={0.8}
                        />
                    </button>
                )}
            </div>
            {isEmpty ? (
                <EmptyInfluences onAdd={openInfluences} />
            ) : (
                <InfluenceGallery display={display} loading={loading} selectedArtists={selectedArtists} />
            )}
        </div>
    );
}

export default Influences;
